
import { ApplicationRef, ComponentRef, createComponent, ElementRef, EnvironmentInjector, inject, Injectable, DOCUMENT } from '@angular/core';
import { arrow, autoPlacement, autoUpdate, computePosition, ComputePositionConfig, detectOverflow, flip, hide, inline, Middleware, offset, shift, size, VirtualElement } from '@floating-ui/dom';
import { MiddlewareOptions, PopoverInstance, PopoverOptions } from '@models/interfaces/components/Popover.interface';

@Injectable({ providedIn: 'root' })
export class PopoverService {

  // SERVICES 
  private readonly appReference:      ApplicationRef                = inject(ApplicationRef);
  private readonly envInjector:       EnvironmentInjector           = inject(EnvironmentInjector);
  private readonly document:          Document                      = inject(DOCUMENT);

  // POPOVER STATE
  private readonly popoverInstances:  Map<string, PopoverInstance>   = new Map<string, PopoverInstance>();
  private readonly popoverOptions:    Partial<ComputePositionConfig> = {
    placement: 'bottom',
    strategy:  'absolute',
    middleware: [],
  };

  // METHODS
  /**
   * Crea un popover y lo añade al DOM, usando un elemento de referencia y contenido HTML o string.
   * Si ya existe un popover con el mismo ID, lo destruye antes de crear uno nuevo.
   *
   * @param popoverId   Identificador único del popover.
   * @param reference   Elemento de referencia para posicionar el popover (HTMLElement, Element, VirtualElement o ElementRef).
   * @param content     Contenido del popover (HTMLElement o string HTML).
   * @param options     Opciones de configuración para el posicionamiento (placement, strategy, middleware, etc).
   * @returns           Instancia del popover creado.
   */
  public createPopover(
    popoverId:    string,
    reference:    HTMLElement | Element | VirtualElement | ElementRef & VirtualElement,
    content:      HTMLElement | string,
    options:      Partial<PopoverOptions> = {},
  ){
    
    // Comprobamos si ya existe una instancia de Popover con el mismo ID
    this.checkPopoverInstance(popoverId ?? '');

    const id:               string                          = popoverId ?? `popover-${Date.now()}`,                                  // ID unico del popover, proporcionado o auto
          referenceElement: unknown                         = reference instanceof ElementRef ? reference.nativeElement : reference, // Elemento de referencia
          popoverElement:   HTMLElement                     = this.createPopoverElement(content),                                    // Elemento Popover     
          finalOptions:     Partial<ComputePositionConfig>  = {                                                                      // Opciones finales de posicionamiento
            placement:  options.placement || this.popoverOptions.placement,
            strategy:   options.strategy  || this.popoverOptions.strategy,
            middleware: options.middleware ? this.setMiddlewares(options.middleware) : this.popoverOptions.middleware,
          };
         
    
    // Añadimos el Popover al DOM
    this.document.body.appendChild(popoverElement);
    
    // Creamos la instancia del Popover
    const instance: PopoverInstance = {
      id,
      element:   popoverElement,
      reference: referenceElement as HTMLElement | Element | VirtualElement,
      update:    async () => this.calculatePosition(instance, finalOptions),
    }

    // Calculamos la posicion del Popover
    this.calculatePosition(instance, finalOptions);

    // Añadimos la función de autoUpdate en la propiedad cleanup del Popover ( se llama al destruir el Popover )
    instance.cleanup = autoUpdate(referenceElement as HTMLElement, popoverElement, ()=> instance.update!())

    // Añadimos la instancia del Popover al mapa de instancias
    this.popoverInstances.set(id, instance);
    
    return instance;

  }
   
  /**
   * Crea un popover que contiene un componente Angular, lo añade al DOM y lo posiciona.
   * Si ya existe un popover con el mismo ID, lo destruye antes de crear uno nuevo.
   *
   * @param popoverId   Identificador único del popover.
   * @param reference   Elemento de referencia para posicionar el popover (HTMLElement, Element, VirtualElement o ElementRef).
   * @param component   Clase del componente Angular a renderizar dentro del popover.
   * @param options     Opciones de configuración para el posicionamiento (placement, strategy, middleware, etc).
   * @returns           Instancia del popover creado, incluyendo la referencia al componente.
   */
  public createPopoverComponent<T>(
    popoverId:  string,
    reference:  HTMLElement | Element | VirtualElement | ElementRef & VirtualElement,
    component:  new (...args: unknown[]) => T,
    inputs?:    Partial<T>, 
    options:    Partial<PopoverOptions> = {},
  ): PopoverInstance {

    // Comprobamos si ya existe una instancia de Popover con el mismo ID
    this.checkPopoverInstance(popoverId ?? '');

    const referenceElement:   unknown                        = reference instanceof ElementRef ? reference.nativeElement : reference, // Elemento de referencia                                
          popoverElement:     HTMLElement                    = this.createPopoverElement(),                                           // Elemento Popover
          componentRef:       ComponentRef<T>                = createComponent(component, { environmentInjector: this.envInjector, hostElement: popoverElement }),  // Instancia del componente Popover
          finalOptions:       Partial<ComputePositionConfig> = {                                                                      // Opciones de posicionamiento                       
            placement:  options.placement || this.popoverOptions.placement,
            strategy:   options.strategy  || this.popoverOptions.strategy,
            middleware: options.middleware ? this.setMiddlewares(options.middleware) : this.popoverOptions.middleware,
          };                        // Opciones de posicionamiento

    // Añadimos el Popover al DOM
    this.document.body.appendChild(popoverElement);

    // Si tiene inputs, los asignamos al componente
    if(inputs) {

      Object.entries(inputs).forEach(([key, value]) => {
        componentRef.setInput(key, value);
      });

    }

    // Encendemos el detectChanges del componente para que se renderice
    componentRef.changeDetectorRef.detectChanges(); 
    
    // Añadimos el componente al DOM
    this.appReference.attachView(componentRef.hostView); 

    // Creamos la instancia del Popover
    const instance: PopoverInstance = {
      id:           popoverId,
      element:      popoverElement,
      reference:    referenceElement as HTMLElement | Element | VirtualElement,
      componentRef: componentRef as ComponentRef<unknown>,
      update:       async () => this.calculatePosition(instance, finalOptions),
    }

    // Calculamos la posicion del Popover
    this.calculatePosition(instance, finalOptions);

    // Proporcionamos la función de autoUpdate en la propiedad cleanup del Popover ( se llama al destruir el Popover )
    instance.cleanup = autoUpdate(referenceElement as HTMLElement, popoverElement, ()=> instance.update!())

    // Añadimos la instancia del Popover al mapa de instancias
    this.popoverInstances.set(popoverId, instance);
    
    return instance;

  }

  /**
   * Destruye un popover existente por su ID, eliminando su elemento del DOM y limpiando recursos.
   *
   * @param id  Identificador del popover a destruir.
  */
  public destroyPopover(id: string): void {

    // Comprobamos si existe la instancia del Popover
    const instance: PopoverInstance | undefined = this.popoverInstances.get(id);
    
    if(!instance) return;

    // Llamamos a la función de cleanup para eliminar el autoUpdate
    instance.cleanup?.(); 

    // Si hay comoponente, lo eliminamos del DOM
    if(instance.componentRef){

      this.appReference.detachView(instance.componentRef.hostView);
      instance.componentRef.destroy();

    }

    // Si hay elemento, lo eliminamos del DOM
    if(instance.element && instance.element.parentNode) {

      instance.element.parentNode.removeChild(instance.element);
      
    }
    
    // Eliminamos instancia del mapa
    this.popoverInstances.delete(id);

  }

  /**
   * Destruye todos los popovers existentes, eliminando sus elementos del DOM y limpiando recursos.
  */
  public destroyAllPopovers(): void {

    this.popoverInstances.forEach((instance: PopoverInstance) => {
      this.destroyPopover(instance.id);
    });

  }

  /**
   * Crea el elemento HTML base del popover, opcionalmente con contenido.
   *
   * @param content  Contenido a insertar en el popover (string, HTMLElement o ElementRef).
   * @returns        Elemento HTML del popover.
   */
  private createPopoverElement(content?: string | HTMLElement | ElementRef ): HTMLElement {

    // Creamos el elemento Popover
    const popoverElement: HTMLElement = this.document.createElement('div');

    // Añadimos la clase Popover al elemento
    popoverElement.classList.add('popover');

    // Si se proporciona contenido, lo añadimos al Popover, si no, lo dejamos vacío
    if (!content) return popoverElement;

    if (typeof content === 'string') {

      popoverElement.innerHTML = content;

    } 
    else {

      const element = content instanceof ElementRef ? 
        content.nativeElement : 
        content;

      popoverElement.appendChild(element as HTMLElement);

    }

    return popoverElement;

  }

  /**
   * Verifica si existe una instancia de popover con el ID dado y la destruye si es así.
   *
   * @param id  Identificador del popover a comprobar.
   */
  private checkPopoverInstance(id: string) {

    if(this.popoverInstances.has(id)) this.destroyPopover(id);

  }

  /**
   * Calcula y aplica la posición del popover respecto a su elemento de referencia,
   * usando las opciones de Floating UI.
   *
   * @param instance  Instancia del popover a posicionar.
   * @param options   Opciones de configuración para el posicionamiento.
   */
  private async calculatePosition(instance: PopoverInstance, options: Partial<ComputePositionConfig> = {}): Promise<void> {

    // Desestructuramos las opciones
    const {placement, strategy, middleware} = options;

    try {
      
      // Calculamos la posicion del Popover en base a la referencia y el elemento Popover y las opciones proporcionadas
      const { x, y } = await computePosition(instance.reference, instance.element, { placement, strategy, middleware });

      // Asignamos la posicion al elemento Popover
      Object.assign(instance.element.style, {
        position:  strategy,
        top:      `${y}px`,
        left:     `${x}px`,
      });

    } catch (error) {

      console.error('Error positioning popover', error);
      
    }

  }

  /**
   * Set Middlewares
   * Método que recibe un objeto de opciones de middleware y devuelve un array de middlewares cargados con sus opciones.
   * 
   * Para ello, necesitamos establecer un orden de aplicación de los middlewares, ya que la computación de la posición
   * depende del estado de los middlewares aplicados anteriormente. Además, como son funciones, hacemos switch para
   * cargar el middleware correspondiente en cada caso a excepción de detectOverflow, que es un middleware especial 
   * que no se aplica directamente, sino que se ejecuta como una función asíncrona sobre el estado devuelto de los middlewares (si los hay).
   * 
   * @param middleware 
   * @returns {Middleware[]}
   */
  private setMiddlewares(middleware: MiddlewareOptions): Middleware[] {

    // Array de Middlewares y orden de aplicación de los mismos
    // https://floating-ui.com/docs/middleware#ordering

    const middlewares: Middleware[]               = [],
          order:      (keyof MiddlewareOptions)[] = ['offset', 'shift', 'flip', 'arrow', 'size', 'autoPlacement','inline', 'hide', 'detectOverflow'];
    
    // Recorremos el array de orden y aplicamos los middlewares en el orden indicado
    order.forEach((key: keyof MiddlewareOptions) => {
      
      // MIDDLEWARES
      if (middleware[key]) {

        switch(key){

          case'offset':
           middlewares.push(offset(middleware.offset));
          break;

          case'shift':
            middlewares.push(shift(middleware.shift));
          break;

          case'flip':
            middlewares.push(flip(middleware.flip));
          break;

          case'arrow':
            middlewares.push(arrow(middleware.arrow!));
          break;

          case'size':
            middlewares.push(size(middleware.size));
          break;

          case'autoPlacement':
            middlewares.push(autoPlacement(middleware.autoPlacement));
          break;

          case'hide':
            middlewares.push(hide(middleware.hide));
          break;

          case'inline':
            middlewares.push(inline(middleware.inline));

          break;

      
        }
        
      }

      // DETECT OVERFLOW MIDDLEWARE
      if(middleware[key] && key === 'detectOverflow') {
        
        // Creamos el middleware detectOverflow de manera customizada
        // https://floating-ui.com/docs/detectOverflow
        const overflowMiddleware: Middleware = {
          
          name: 'detectOverflow',
          
          async fn(state) {
            await detectOverflow(state, middleware.detectOverflow);
            return {}
          }

        }

        middlewares.push(overflowMiddleware);
        
      }

    })
    
    return middlewares;

  }
    
}
